import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Chat from "@shared/components/Assistant/Assistant";
import { useProjectChat } from "@services/ProjectChatContext";
import { ChartData } from "@shared/interfaces/Charts";
import { ReportDetailed, ReportFile } from "@shared/interfaces/ReportsFiles";
import { ReportHeader } from "@pages/Report/components/ReportHeader";
import ReportEditor from "@pages/Report/components/ReportEditor";
import { toastService } from "@services/ToastService";
import { ReportContent } from "@pages/Report/components/ReportContent";
import { hasPermission, Permission } from "@utils/permissions";
import { useUser } from "@services/UserContext";
import { MapProvider } from "@shared/components/Mapbox/MapContext";
import MapBox from "@shared/components/Mapbox/Mapbox";

import { useProject } from "@utils/useProject";
import { useReport } from "@utils/useReports";
import ThumbButtons from "@shared/components/Feedback/ThumbButtons";
import { useFeedback } from "@shared/components/Feedback/utils/useFeedback";
import FeedbackSubmission from "@shared/components/Feedback/FeedbackSubmission";
import checkCircle from "@assets/check_circle_blue.svg";
import arrowLeft from "@assets/arrow_left.svg";
import { ReportProject } from "@shared/interfaces/ProjectsFiles";
import ReportAssistant from "./components/ReportAssistant";
import Loader from "@shared/components/Loader";
import loadingAnimation from "@assets/loading_anim.json";

import Lottie from "lottie-react";
import { wsBaseUrl } from "@constants";
import LoadingBar from "@shared/components/LoadingBar";

const ReportPage: React.FC = () => {
  const [reportResult, setReportResult] = useState<ReportDetailed>();
  const location = useLocation();
  const [files, setFiles] = useState<ReportFile[]>([]);
  const [charts, setCharts] = useState<ChartData[]>([]);
  const sharedToken =
    location.state?.sharedToken ||
    useParams<{ sharedToken: string }>().sharedToken ||
    "";
  const { projectId } = useParams<{ projectId: string }>();
  const { reportId } = useParams<{ reportId: string }>();
  const [showAssistant, setShowAssistant] = useState(false);
  const [chatLog, setChatLog] = useState<
    { message: string; sender: "user" | "bot" }[]
  >([]);
  const { setProject: setProjectChat } = useProjectChat();
  const [isEditing, setIsEditing] = useState(
    location.state?.isEditing || false
  );
  const [editorContent, setEditorContent] = useState<string>("");
  const [editorTitle, setEditorTitle] = useState<string>("");
  const [isReportViewer, setIsReportViewer] = useState(false);
  const navigation = useNavigate();

  //permisions
  const { user } = useUser();
  const [canEdit, setCanEdit] = useState(false);
  const [canComment, setCanComment] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const [canViewReportFiles, setCanViewReportFiles] = useState(false);
  const [canViewProjectFiles, setCanViewProjectFiles] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isReportEditing, setIsReportEditing] = useState(false);
  const [reportEditingStatus, setReportEditingStatus] = useState("Processing");
  const [reportEditingState, setReportEditingState] = useState("PENDING");

  const [project, setProject] = useState<ReportProject>({} as ReportProject);

  const { getProjectById, getSharedProject } = useProject();
  const { getReport, updateReport } = useReport();

  const [progress, setProgress] = useState(0);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startProgressTimer = (seconds: number) => {
    // Reset progress
    setProgress(0);

    const totalDuration = seconds * 1000;
    const intervalDuration = 1000; // 1 second
    const increment = 100 / (totalDuration / intervalDuration); // ~0.033%

    // Clear existing timer
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
    }

    progressTimerRef.current = setInterval(() => {
      setProgress((prev) => {
        const next = prev + increment;
        return next >= 99 ? 99 : next;
      });
    }, intervalDuration);
  };

  const stopProgressTimer = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    setProgress(100); // mark complete
  };

  const setReport = async (fetchedReport: ReportDetailed) => {
    try {
      if (fetchedReport && projectId && reportId) {
        setProject({
          id: fetchedReport.project.id,
          name: fetchedReport.project.name,
        });
        setProjectChat(
          projectId,
          fetchedReport.project.name,
          reportId,
          editorTitle
        );
      }
    } catch (error) {
      try {
        if (sharedToken) {
          const sharedProject = await getSharedProject(sharedToken);

          if (sharedProject) {
            setProjectChat(
              sharedProject.id,
              sharedProject.name,
              reportId,
              editorTitle
            );
            setIsReportViewer(true);
          }
        }
      } catch (error) {
        console.error("Error fetching shared project:", error);
      }
    }
  };

  const reportEditing = () => {
    setIsReportEditing(true);
  };

  useEffect(() => {
    const savedChatLog = localStorage.getItem("chatLog");
    if (savedChatLog) {
      setChatLog(JSON.parse(savedChatLog));
    }
  }, []);

  const navigate = useNavigate();

  const handleReportReceive = async () => {
    try {
      const report = await getReport(
        projectId ?? "",
        reportId ?? "",
        sharedToken
      );

      if (report) {
        setEditorTitle(report.name);
        setReportResult(report);
        setFiles(report.files || []);
        setCharts(report.plots_data?.charts || []);

        return report;
      }
    } catch (error: any) {
      if (error.status === 403) {
        navigate("/assistant");
      }
      console.error("Error fetching report:", error);
    }
  };

  const openReportEditingSocket = (taskId: string) => {
    setProgress(0);
    const ws_re = new WebSocket(`${wsBaseUrl}/task/${taskId}`);
    ws_re.onmessage = async (event_re) => {
      const data_re = JSON.parse(event_re.data);
      if (data_re.state !== "" || data_re.state !== null) {
        console.log("data_re.state", data_re.state);

        setReportEditingState(data_re.state);
        if (data_re.state === "PENDING" || data_re.state === "PROGRESS") {
          if (!progressTimerRef.current) {
            if (data_re.type === "REPORT_SIMPLE_EDIT") {
              startProgressTimer(30);
            } else if (data_re.type === "REPORT_DATA_ACCESS_EDIT") {
              startProgressTimer(240);
            }
          }

          reportEditing();

          if (data_re.current_step !== "" && data_re.current_step !== null)
            setReportEditingStatus(data_re.current_step);
        } else if (data_re.state === "SUCCESS") {
          console.log("here");
          stopProgressTimer();
          setTimeout(() => {
            reportEditingComplete();
          }, 1250);
        } else if (data_re.state === "FAILURE") {
          stopProgressTimer();
          reportEditingComplete();
        }
      }
    };
  };

  useEffect(() => {
    (async () => {
      if (reportId && projectId) {
        const report = await handleReportReceive();
        setReport(report);
      }
    })();
  }, [reportId, projectId]);

  const reportEditingComplete = async () => {
    await handleReportReceive();
    setIsReportEditing(false);
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const saveChanges = async () => {
    try {
      if (
        !editorContent ||
        editorContent.length === 0 ||
        editorContent === "" ||
        editorContent === null
      ) {
        if (editorTitle.length === 0 || editorTitle === reportResult?.name) {
          toastService.warn("No content to save");
          setIsEditing(false);
          return;
        } else {
          if (!projectId || !reportId) {
            console.error("Missing projectId or reportId");
            return;
          }
          await updateReport(projectId, reportId, {
            name: editorTitle,
          });
        }
      } else {
        if (!projectId || !reportId) {
          console.error("Missing projectId or reportId");
          return;
        }
        await updateReport(projectId, reportId, {
          content: editorContent,
          name: editorTitle,
        });
      }

      await handleReportReceive();
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  const discardChanges = () => {
    setEditorContent(reportResult?.content || "");
    setEditorTitle(reportResult?.name || "");
    setIsEditing(false);
  };

  const isReportOwner = reportResult?.creator?.id === user?.id;

  useEffect(() => {
    const getPermissions = () => {
      if (reportResult != undefined && user) {
        setCanShare(
          hasPermission(
            reportResult.permissions,
            Permission.CAN_MANAGE_ACCESS
          ) || isReportOwner
        );
        setCanEdit(
          hasPermission(
            reportResult.permissions,
            Permission.CAN_EDIT_REPORTS
          ) || isReportOwner
        );
        setCanComment(
          hasPermission(
            reportResult.permissions,
            Permission.CAN_LEAVE_COMMENTS
          ) || isReportOwner
        );
        setCanViewReportFiles(
          hasPermission(
            reportResult.permissions,
            Permission.CAN_VIEW_REPORTS_FILES
          ) || isReportOwner
        );
        setCanViewProjectFiles(
          hasPermission(reportResult.permissions, Permission.CAN_VIEW_FILE) ||
            isReportOwner
        );

        if (
          !hasPermission(reportResult.permissions, Permission.CAN_VIEW) &&
          !isReportOwner
        ) {
          toastService.error("Unauthorized");
          navigation("/assistant");
        }
      }
    };

    getPermissions();
  }, [reportResult, project]);

  const [showFeedback, setShowFeedback] = useState(true);
  const [showThankyouText, setShowThankyouText] = useState(false);

  const feedbackSubmitted = reportResult?.feedback_by_user === true;
  const {
    feedbackText,
    setFeedbackText,
    feedbackOption,
    setFeedbackOption,
    feedbackState,
    setFeedbackState,
    feedbackSubmittedFor,
    selectedThumbs,
    feedbackHiddenFor,
    feedbackUpdating,
    handleThumbClick,
    handleFeedbackSubmit,
    feedbackForIndex,
  } = useFeedback("", true);

  useEffect(() => {
    if (feedbackSubmittedFor[99]) {
      setShowFeedback(false);
    }
  }, [feedbackSubmittedFor]);

  useEffect(() => {
    if (!showFeedback) {
      setShowThankyouText(true);
      setTimeout(() => {
        setShowThankyouText(false);
      }, 3000);
    }
  }, [showFeedback]);

  const isReportHeaderVisible = canShare || canEdit || canViewProjectFiles;

  return (
    <div className="flex flex-col max-h-[calc(100vh-78px)] ">
      <div className="flex w-full max-h-[calc(100vh-78px)] ">
        {reportResult != undefined ? (
          <>
            <main className="flex-1 w-full p-0 max-h-[calc(100vh-78px)] ">
              <div className="w-full flex justify-between max-h-[calc(100vh-78px)] overflow-hidden">
                <div className="w-full">
                  <ReportHeader
                    toggleEdit={toggleEdit}
                    isEditing={isEditing}
                    saveChanges={saveChanges}
                    discardChanges={discardChanges}
                    sharedToken={sharedToken}
                    isReportViewer={isReportViewer}
                    canEdit={canEdit}
                    canShare={canShare}
                    canViewProjectFiles={canViewProjectFiles}
                    report={reportResult}
                  />
                  <div
                    className={`w-full flex flex-col overflow-y-auto ${
                      isReportHeaderVisible
                        ? "max-h-[calc(100vh-82px)]"
                        : "max-h-[100vh]"
                    } max-w-[75%] mx-auto scrollbar-hide`}
                  >
                    {reportResult?.geojson_data ? (
                      <div
                        className="h-[210px] 2xl:h-[250px] w-full"
                        id="mapContainerDivReport"
                      >
                        <MapProvider>
                          <MapBox
                            originalGeoJson={reportResult?.geojson_data}
                            isEditing={false}
                          />
                        </MapProvider>
                      </div>
                    ) : null}
                    {isEditing ? (
                      <ReportEditor
                        initialContent={reportResult?.content || ""}
                        setEditorContent={setEditorContent}
                        initialTitle={reportResult?.name || editorTitle}
                        setEditorTitle={setEditorTitle}
                        plotsData={{ charts }}
                        created_at={reportResult?.created_at}
                        template_name={reportResult?.template_name}
                      />
                    ) : (
                      <>
                        {!isReportEditing && (
                          <>
                            {showThankyouText && (
                              <div className="w-full flex justify-center items-center pt-5 report-additional-header">
                                <div className="text-sm text-gray-800 flex items-center space-x-2 border border-gray-300 rounded-lg shadow-sm p-4">
                                  <img
                                    src={checkCircle}
                                    className="w-5 h-5"
                                    alt=""
                                  />
                                  <p>Thank you for your feedback!</p>
                                </div>
                              </div>
                            )}
                            {showFeedback &&
                              isReportOwner &&
                              !feedbackSubmitted && (
                                <div className="w-[calc(100%-120px)] mx-auto border border-gray-300 rounded-md p-2 mt-6 mb-0 text-sm 2xl:text-base report-additional-header">
                                  {!feedbackSubmittedFor[99] && (
                                    <div className="flex justify-center items-center space-x-3  p-2">
                                      <div>
                                        {feedbackForIndex === 99 &&
                                        !feedbackHiddenFor[99] ? (
                                          <span className="flex space-x-3">
                                            {feedbackState.showInput && (
                                              <button
                                                onClick={() =>
                                                  setFeedbackState((prev) => ({
                                                    ...prev,
                                                    showInput: false,
                                                  }))
                                                }
                                                className="flex items-center text-sm text-[#2563eb]"
                                              >
                                                <img
                                                  src={arrowLeft}
                                                  className="w-4 h-4"
                                                  alt="Back"
                                                />
                                              </button>
                                            )}
                                            <p>
                                              {feedbackState.type === "like"
                                                ? "What did you like about this response?"
                                                : "What didn't you like about this response?"}
                                            </p>
                                          </span>
                                        ) : (
                                          <span>
                                            Did the generated report meet your
                                            expectations?
                                          </span>
                                        )}{" "}
                                      </div>

                                      <div className="mt-[-1rem]">
                                        <ThumbButtons
                                          index={99}
                                          selectedThumbs={selectedThumbs}
                                          feedbackSubmittedFor={
                                            feedbackSubmittedFor
                                          }
                                          handleThumbClick={handleThumbClick}
                                          feedbackSubmitted={feedbackSubmitted}
                                          feedbackUpdating={feedbackUpdating}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {feedbackForIndex === 99 &&
                                    !feedbackHiddenFor[99] && (
                                      <FeedbackSubmission
                                        index={99}
                                        feedbackSubmittedFor={
                                          feedbackSubmittedFor
                                        }
                                        feedbackState={feedbackState}
                                        feedbackOption={feedbackOption}
                                        feedbackText={feedbackText}
                                        feedbackUpdating={feedbackUpdating}
                                        setFeedbackText={setFeedbackText}
                                        setFeedbackOption={setFeedbackOption}
                                        setFeedbackState={setFeedbackState}
                                        handleFeedbackSubmit={
                                          handleFeedbackSubmit
                                        }
                                        hideFeedbackTitle={true}
                                        className={
                                          "bg-[#fff] mt-[-5px!important]"
                                        }
                                        feedbackSubmitted={feedbackSubmitted}
                                      />
                                    )}
                                </div>
                              )}
                          </>
                        )}
                        {isReportEditing ? (
                          <div
                            className={`w-full ${
                              isReportHeaderVisible
                                ? "h-[calc(100vh-82px)]"
                                : "h-[100vh]"
                            } flex flex-col justify-center items-center pb-28 self-center`}
                          >
                            <Lottie
                              animationData={loadingAnimation}
                              style={{ width: 400 }}
                            />

                            <div className="relative w-[360px]">
                              <div className=" w-[360px] absolute top-[-40px] z-10 ">
                                <LoadingBar
                                  progress={
                                    reportEditingState === "SUCCESS"
                                      ? 100
                                      : progress
                                  }
                                  failed={reportEditingState === "FAILURE"}
                                  message={
                                    reportEditingState === "SUCCESS"
                                      ? "Completed ✅"
                                      : reportEditingState === "PENDING"
                                      ? "Pending"
                                      : reportEditingStatus || ""
                                  }
                                  notification={false}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <ReportContent
                            report={reportResult}
                            plotsData={{ charts }}
                            canComment={canComment}
                            isReportEditing={isReportEditing}
                            isReportAssistantCollapsed={isCollapsed}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
                <ReportAssistant
                  isCollapsed={isCollapsed}
                  setIsCollapsed={setIsCollapsed}
                  isReportEditing={isReportEditing}
                  openReportEditingSocket={openReportEditingSocket}
                  isReportHeaderVisible={isReportHeaderVisible}
                  isEditing={isEditing}
                />
              </div>
            </main>
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-screen">
            <Loader size="lg" color="#0056C8" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPage;
